import { useEffect, useRef, useState } from 'react';

interface MatrixRainProps {
  className?: string;
  opacity?: number;
}

// Detect device capabilities for performance optimization
const getDevicePerformanceLevel = () => {
  if (typeof window === 'undefined') return 'high';

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isLowEnd = navigator.hardwareConcurrency ? navigator.hardwareConcurrency <= 4 : isMobile;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) return 'none';
  if (isLowEnd || isMobile) return 'low';
  return 'high';
};

export default function MatrixRain({ className = '', opacity = 0.15 }: MatrixRainProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const performanceLevel = useRef<string>('high');

  // Visibility-based pause using Intersection Observer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });
    if (!ctx) return;

    // Detect performance level
    performanceLevel.current = getDevicePerformanceLevel();
    if (performanceLevel.current === 'none') return;

    // White rabbit themed binary messages (reduced set)
    const messages = [
      '01000110 01001111 01001100 01001100 01001111 01010111', // FOLLOW
      '01010111 01001000 01001001 01010100 01000101', // WHITE
      '01010010 01000001 01000010 01000010 01001001 01010100', // RABBIT
      '🐰',
      '01000010 01000001 01001110 01000111 01000101 01010010 01010011', // BANGERS
    ];

    // Characters to use (binary only - removed complex matrix chars)
    const chars = '01';

    // Cyber colors
    const colors = [
      { r: 0, g: 212, b: 255 },   // cyber-cyan
      { r: 139, g: 92, b: 246 },  // cyber-purple
      { r: 236, g: 72, b: 153 },  // cyber-pink
    ];

    const fontSize = performanceLevel.current === 'low' ? 14 : 12;
    let columns: number = 0;
    let drops: number[] = [];
    let colorIndices: number[] = [];
    let speeds: number[] = [];
    let messageDrops: { col: number; chars: string[]; index: number; active: boolean }[] = [];

    let resizeTimeout: NodeJS.Timeout;
    const resize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        columns = Math.floor(canvas.width / fontSize);

        // Initialize drops if not exists or columns changed
        if (!drops || drops.length !== columns) {
          drops = Array(columns).fill(0).map(() => Math.random() * -100);
          colorIndices = Array(columns).fill(0).map(() => Math.floor(Math.random() * colors.length));
          speeds = Array(columns).fill(0).map(() => 0.3 + Math.random() * 0.7);
          messageDrops = [];
        }
      }, 150);
    };

    resize();
    window.addEventListener('resize', resize);

    // Occasionally spawn a message in a column
    const spawnMessage = () => {
      if (messageDrops.length < Math.floor(columns / 12) && Math.random() < 0.015) {
        const col = Math.floor(Math.random() * columns);
        const msg = messages[Math.floor(Math.random() * messages.length)];
        messageDrops.push({
          col,
          chars: msg.split(''),
          index: 0,
          active: true
        });
      }
    };

    const draw = () => {
      // Skip drawing when not visible or not initialized
      if (!isVisible || columns === 0) return;

      // Semi-transparent black to create fade effect
      ctx.fillStyle = `rgba(5, 5, 16, 0.05)`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${fontSize}px monospace`;

      // Adjust trail length based on performance
      const trailLength = performanceLevel.current === 'low' ? 5 : 10;

      // Draw regular falling characters (optimized - fewer columns processed)
      for (let i = 0; i < columns; i++) {
        const color = colors[colorIndices[i]];
        const y = drops[i] * fontSize;

        // Skip if this column has an active message
        const hasMessage = messageDrops.some(m => m.col === i && m.active);
        if (hasMessage) continue;

        // Random binary character
        const char = chars[Math.floor(Math.random() * chars.length)];

        // Leading character is brighter
        ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, 0.9)`;
        ctx.fillText(char, i * fontSize, y);

        // Optimized trail rendering
        for (let j = 1; j < trailLength; j++) {
          const trailY = y - j * fontSize;
          if (trailY < 0) continue;

          const trailOpacity = Math.max(0, (trailLength - j) / trailLength * 0.5);
          ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${trailOpacity})`;
          const trailChar = chars[Math.floor(Math.random() * chars.length)];
          ctx.fillText(trailChar, i * fontSize, trailY);
        }

        // Move drop
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
          colorIndices[i] = Math.floor(Math.random() * colors.length);
          speeds[i] = 0.3 + Math.random() * 0.7;
        }
        drops[i] += speeds[i];
      }

      // Draw message drops
      for (let m = 0; m < messageDrops.length; m++) {
        const msg = messageDrops[m];
        if (!msg.active) continue;

        const color = colors[1]; // Use purple for messages
        const x = msg.col * fontSize;
        
        // Draw current character brightly
        if (msg.index < msg.chars.length) {
          const y = msg.index * fontSize;
          ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, 1)`;
          ctx.fillText(msg.chars[msg.index], x, y);

          // Draw trail of previous chars
          for (let j = 0; j < msg.index; j++) {
            const trailY = j * fontSize;
            const trailOpacity = Math.max(0.1, (msg.index - j) / msg.chars.length * 0.6);
            ctx.fillStyle = `rgba(${color.r}, ${color.g}, ${color.b}, ${trailOpacity})`;
            ctx.fillText(msg.chars[j], x, trailY);
          }

          msg.index++;
        } else {
          // Message complete, fade it out
          msg.active = false;
        }
      }

      // Clean up inactive messages
      messageDrops = messageDrops.filter(m => m.active);

      spawnMessage();
    };

    // Adjust frame rate based on device performance
    const frameRate = performanceLevel.current === 'low' ? 90 : 66; // ~11fps for low-end, ~15fps for high-end
    intervalRef.current = setInterval(draw, frameRate);

    return () => {
      clearTimeout(resizeTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('resize', resize);
    };
  }, [isVisible]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ opacity }}
    />
  );
}

